import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  AppBar,
  Toolbar,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ReactComponent as LogoTagline } from '../assets/logo.svg';
import Grid from '@mui/material/Grid2';
import { Navigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const Login = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include'
        });
        setIsAuthenticated(response.ok);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Invalid email or password");
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred during login');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fe',
        }}
      >
        <Typography variant="h6" color="#1976d2">Loading...</Typography>
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: '#f8f9fe',
        position: 'relative',
        opacity: 0,
        animation: 'fadeIn 1s forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)'
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: { xs: 0.5, md: 1 },
            px: { xs: 2, md: 3 },
            minHeight: { xs: '56px', md: '64px' }
          }}
        >
          <LogoTagline style={{
            width: 'auto',
            height: '36px',
            maxWidth: '220px',
            cursor: 'pointer'
          }} onClick={() => window.location.href = '/'} />
          <Button
            variant="contained"
            color="primary"
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 0.5, md: 1 },
              fontSize: { xs: '0.875rem', md: '0.95rem' },
              fontWeight: 500,
              whiteSpace: 'nowrap',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.4)'
              }
            }}
            onClick={() => window.location.href = '/#signup-section'}
          >
            Sign Up
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 4, md: 8 },
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-50%',
            width: '200%',
            height: '100%',
            background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.03) 0%, transparent 70%)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6} sx={{ 
              textAlign: 'center',
              mb: { xs: 2, md: 0 } 
            }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #0A2540 0%, #1976d2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  color: '#546E7A',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.5,
                  fontWeight: 400
                }}
              >
                Log in to access your dashboard and continue analyzing your customer reviews
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                  borderRadius: '24px',
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                  p: 4,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 20px 48px -12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-4px)'
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
                  }
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    color: '#0A2540',
                    textAlign: 'center',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Log In to Your Account
                </Typography>
                
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%',
                  }}
                >
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#1976d2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)'
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.95rem'
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#1976d2' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)'
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 15px -3px rgba(25, 118, 210, 0.2)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.95rem'
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!email || !password}
                    sx={{
                      mt: 2,
                      py: 2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                      boxShadow: '0 8px 20px -6px rgba(25, 118, 210, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                        boxShadow: '0 12px 24px -8px rgba(25, 118, 210, 0.6)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Log In
                  </Button>

                  {/* Forgot Password Link */}
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                    <Button
                      variant="text"
                      sx={{
                        p: 0,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#1976d2',
                        '&:hover': { 
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={() => window.location.href = '/forgot-password'}
                    >
                      Forgot Password?
                    </Button>
                  </Typography>
                  
                  <Typography variant="body1" sx={{
                    mt: 3,
                    textAlign: 'center',
                    color: '#546E7A'
                  }}>
                    Don't have an account?{' '}
                    <Button
                      variant="text"
                      sx={{
                        p: 0,
                        ml: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#1976d2',
                        '&:hover': { 
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={() => window.location.href = '/#signup-section'}
                    >
                      Sign Up
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
